import React, { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { AtticusForm } from "../../models/AtticusForm";
import { HelpDialog } from "./HelpDialog";
import { CONSTANTS as SHARED_CONSTANTS } from "@theatticusapp/atticus-shared-components";
import { FormItemConnector } from "./FormItemConnector";
import { State } from "../../models/Geography";
import Cookies from "js-cookie";

interface Props {
  form: AtticusForm;
  onShowForm?: (form: AtticusForm) => void;
  state?: State;
  disabled?: boolean;
  displayHelp?: boolean;
  displayThumbnail?: boolean;
  verticalPaddingUnit?: number;
  displayConnector?: boolean;
  firstItem?: boolean;
  lastItem?: boolean;
  onlyOne?: boolean;
  displaySeparator?: boolean;
}

export const FormItem = ({
  form,
  onShowForm,
  state,
  disabled = false,
  displayHelp = false,
  displayThumbnail = false,
  verticalPaddingUnit = 2,
  displayConnector = false,
  firstItem = false,
  lastItem = false,
  onlyOne = false,
  displaySeparator = false,
}: Props): JSX.Element => {
  const [helpOpen, setHelpOpen] = useState(false);

  const handleDownloadForm = async (): Promise<void> => {
    if (form?.getFilledFormUrl) {
      const authToken = Cookies.get(SHARED_CONSTANTS.COOKIES.authToken) || "";
      const url = form.getFilledFormUrl(authToken);
      if (url) {
        window.open(url);
      }
    }
  };

  const buttonStyles = {
    px: 2,
    minWidth: displayConnector ? 120 : 180,
    width: displayConnector ? 120 : 180,
  } as const;

  const getHelpTitle = (): string => {
    return `${form?.governmentId ? `[${form?.governmentId}] ` : ""} ${
      form?.formTitle
    } `;
  };

  const thumbnailAlt = `Thumbnail of ${state?.code || ""} probate form ${
    form?.governmentId
  }`;

  return (
    <>
      <HelpDialog
        open={helpOpen}
        onConfirm={() => setHelpOpen(false)}
        content={form?.formDescription}
        title={getHelpTitle()}
      />
      <Box
        id="FormItem"
        sx={{
          display: "flex",
        }}
      >
        {displayConnector && (
          <FormItemConnector
            first={firstItem}
            last={lastItem}
            onlyOne={onlyOne}
          />
        )}
        <Box
          id="FormItemRight"
          sx={{
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              my: verticalPaddingUnit,
              flexDirection: {
                xs: "column",
                sm: "row",
              },
            }}
          >
            {displayThumbnail && (
              <Box
                sx={{
                  width: 93,
                  border: "solid 2px #ccc",
                  display: {
                    sm: "block",
                    xs: "none",
                  },
                  alignSelf: "flex-start",
                }}
              >
                <img
                  src={form?.s3Urls?.thumbnailFile}
                  alt={thumbnailAlt}
                  title={thumbnailAlt}
                  style={{ width: "100%" }}
                />
              </Box>
            )}
            <Box
              sx={{
                mx: 2,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                alignSelf: "flex-start",
              }}
            >
              {form?.formTitle && (
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {form.formTitle}
                  {displayHelp && form?.formDescription && (
                    <IconButton
                      aria-label="help"
                      onClick={() => setHelpOpen(true)}
                    >
                      <HelpOutlineIcon fontSize="inherit" color="secondary" />
                    </IconButton>
                  )}
                </Typography>
              )}
              {form?.governmentId && (
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  {form.governmentId}
                </Typography>
              )}
              {form?.formDescription && !displayHelp && (
                <Typography variant="body2">{form.formDescription}</Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifySelf: "flex-end",
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  onShowForm && onShowForm(form);
                }}
                size="small"
                sx={buttonStyles}
                disabled={disabled}
              >
                View
              </Button>
              <Button
                // variant="contained"
                onClick={() => {
                  handleDownloadForm();
                }}
                size="small"
                sx={buttonStyles}
                disabled={disabled}
              >
                Download
              </Button>
            </Box>
          </Box>
          {displaySeparator && !lastItem && !onlyOne && (
            <hr
              color="#ccc"
              style={{ height: "1px", width: "100%", border: "none" }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
